<template>
  <div id="bidding-tab" class="tab">

    <div class="info is-flex">
      <div class="column has-margin-right is-half-desktop is-two-fifths-widescreen is-one-third-fullhd is-full-tablet is-full-mobile p-0" v-if="!publicProjectView">
        <div class="label">Invited to Bid On</div>
        <div class="box overflow-scroll">
          <div class="overflow-wrap">{{company?.primary_contact?.first_name}} {{company?.primary_contact?.last_name}} with {{company?.name}}</div>
          <div class="has-margin-top-large overflow-wrap" v-if="hasUnconfirmed">Will you be bidding?</div>
          <div class="box trade has-margin-top-large is-relative p-3" v-for="bidder in bidders" :class="{'error': error(bidder.id, 'confirmed')}">
            <div class="label" v-if="!bidder.trade && bidPackageId">{{ bidPackage?.name }}</div>
            <div class="label" v-else-if="bidder.trade">
              <div>{{ bidder.trade.csi }}: {{ bidder.trade.division_name }} - {{ bidder.trade.name }}</div>
            </div>
            <div class="label" v-else-if="project">{{ project.name }}</div>

            <o-field class="bid-response-control" :variant="error(bidder.id, 'confirmed') ? 'danger' : ''"
                     :message="error(bidder.id, 'confirmed')"
            >
              <o-button size="small" icon-left="thumbs-up" class="is-one-third" :class="{'is-primary': isBidderAnswer(bidder, 1)}" @click="handleUpdate(bidder, 1)">Yes</o-button>
              <o-button size="small" icon-left="thumbs-down" class="is-one-third" :class="{'is-primary': isBidderAnswer(bidder, 2)}" @click="handleUpdate(bidder, 2)">No</o-button>
              <o-button size="small" icon-left="circle-question" class="is-one-third" :class="{'is-primary': isBidderAnswer(bidder, 3)}" @click="handleUpdate(bidder, 3)">Unsure</o-button>
            </o-field>
          </div>
        </div>
      </div>

      <div class="column is-full-tablet is-full-mobile" :class="{'is-half-desktop is-three-fifths-widescreen is-two-thirds-fullhd p-0': !publicProjectView, 'is-full-desktop is-full-thirds-fullhd is-full-thirds-widescreen': publicProjectView}">
        <div class="label">Project Files</div>
        <div class="box is-relative p-0 project-files-container">
          <div v-if="showFiles && bidPackageFileStore.isLoading" class="plan-room-loading">
            <LoadingIndicator />
          </div>
          <div v-else-if="showFiles && hasFiles">
            <PlanroomFileTree class="project-file-tree"
                      :files="files"
                      :download-all-files="downloadFiles"
                      :download-selected-files="downloadSelectedFiles"
                      :download-file="downloadFile"
                      :default-expanded-state="true"
            />
          </div>
          <div v-else class="overflow-wrap project-files-empty">
            <HTMLText :value="project?.exclusions || 'None'" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">

import {
  NotificationSuccess,
  NotificationError,
} from "@/internal";
import {computed, ref, toRefs} from "vue";
import {storeToRefs} from "pinia";
import PlanroomFileTree from '@/components/planroom/PlanroomFileTree.vue';
import type {ProjectBidder} from "@/models/ProjectBidder";
import HTMLText from "@/components/HTMLText.vue";
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import {useBidPackageProjectBidderStore} from "@/stores/BidPackageProjectBidderStore";
import {useCompanyStore} from "@/stores/CompanyStore";
import {useProjectStore} from "@/stores/ProjectStore";
import {usePublicProjectStore} from "@/stores/PublicProjectStore";
import {useBidPackageStore} from "@/stores/BidPackageStore";
import {useBidPackageFileStore} from "@/stores/BidPackageFileStore";
import type {Trade} from "@/models/Trade";
import {DateTime} from "luxon";

const bidPackageProjectBidderStore = useBidPackageProjectBidderStore();
const bidPackageFileStore = useBidPackageFileStore();
const { bidPackageProjectBidders } = storeToRefs(useBidPackageProjectBidderStore());
const { bidPackageFiles } = storeToRefs(useBidPackageFileStore());
const { bidPackage } = storeToRefs(useBidPackageStore());
const { company } = storeToRefs(useCompanyStore());
const errors = ref(<Record<any, any>>[]);
const { project: authenticatedProject } = storeToRefs(useProjectStore());
const { project: publicProject } = storeToRefs(usePublicProjectStore());
const answers = ref<Record<any, number>>({});
const tradeAnswers = ref<Record<any, number|null>>({});
const packageAnswer = ref<number|null>(null);
const awarded = ref<boolean>(false);
const isLoading = ref<boolean>(false);

const props = withDefaults(defineProps<{
  bidPackageId: number | null,
  publicProjectView?: boolean,
}>(), {
  bidPackageId: null,
  publicProjectView: false,
});
const {
  bidPackageId,
  publicProjectView,
} = toRefs(props);

const project = computed(() => publicProjectView.value ? publicProject.value : authenticatedProject.value);

const emit = defineEmits<{
  'projectBidderUpdated': [],
}>();

const bidders = computed<ProjectBidder|any[]>(() => {
  const trades: (number | null)[] = [];
  answers.value = {};
  tradeAnswers.value = {};
  packageAnswer.value = null;
  awarded.value = false;

  let bidders = bidPackageProjectBidders.value?.filter((bidder: ProjectBidder) => {
    if (bidder.bid_awarded) {
      awarded.value = true;
    }

    if ((bidder.trade && trades.includes(bidder.trade.id)) || (!bidder.trade && trades.includes(null))) {
      return false;
    } else {
      answers.value[bidder.id] = bidder.confirmed;
      trades.push(bidder.trade ? bidder.trade.id : null);
      return true;
    }
  }) || [];

  const availableTrades = bidPackageId.value
    ? (bidPackage.value?.trades || [])
    : (project.value?.open_invitation ? company.value?.trades : []);

  if (availableTrades?.length) {
      bidders = bidders.concat(availableTrades.filter((trade: Trade) => !trades.includes(trade.id)).map((trade: Trade) => {
        tradeAnswers.value[trade.id] = null;
        return {
          id: trade.id,
          trade: trade,
          type: "trade",
        };
      }));
  }
  else if (bidPackageId.value && !trades.includes(null)) {
    bidders.push({
      id: 0,
      trade: null,
      type: "package",
    });
  }

  return bidders;
});

const files = computed(() => bidPackageFiles.value || []);

const hasFiles = computed(() => !!files.value.length);

const hasUnconfirmed = computed(() => bidders.value.some(
  (bidder: ProjectBidder) => bidder.confirmed === 0,
));

const isOpenProjectAndUninvited = computed(() => project.value?.open_invitation && !project.value.invited);

const showFiles = computed(() => (!publicProjectView.value && project.value?.limit_opr_to_awarded && awarded.value) || !project.value?.limit_opr_to_awarded);

function clearErrors() {
  errors.value = [];
}

function downloadSelectedFiles(selectedFiles: any) {
  if (!selectedFiles || !selectedFiles.length) {
    NotificationError({message: "Select Files to Download"});
  }
  else {
    const selectedIds = selectedFiles.map((file: any) => file.id);
    downloadFiles(selectedIds);
  }
}

function downloadFiles(selectedFiles: number[]|null) {
  if (!project.value || (bidPackageId.value === null && !publicProjectView.value)) {
    return;
  }

  isLoading.value = true;

  const payload: any = {
    files: selectedFiles,
  };

  const filename = DateTime.local().toFormat('yyyy-LL-dd') + "-" + project.value.name;

  bidPackageFileStore.zip(project.value.id, bidPackageId.value ?? 0, filename, payload, publicProjectView.value)
    .catch(error => {
      if (error.errors) {
        NotificationError({}, error);
      } else {
        NotificationError({
          message: error,
        });
      }
    }).finally(() => {
      isLoading.value = false;
    });
}

function downloadFile(file: any) {
  if (!project.value || !file?.id || (bidPackageId.value === null && !publicProjectView.value)) {
    return;
  }

  bidPackageFileStore.download(project.value.id, bidPackageId.value ?? 0, file, publicProjectView.value)
    .catch(error => {
      if (error.errors) {
        NotificationError({}, error);
      }
      else {
        NotificationError({message: error});
      }
    });
}

function error(projectBidderId: number | string, key: string) {
  return errors.value?.find((error: any) => error.hasOwnProperty('key') && (parseInt(error.forId) === <number>projectBidderId) && (error.key === key))?.detail || '';
}

function isBidderAnswer(bidder: any, answer: number) {
  if (bidder.hasOwnProperty('type') && bidder.type === "trade") {
    return tradeAnswers.value[bidder.id] === answer;
  }
  else if (bidder.hasOwnProperty('type') && bidder.type === "package") {
    return packageAnswer.value === answer;
  }
  else {
    return answers.value[bidder.id] === answer;
  }
}

function handleUpdate(bidder: any, answer: number) {
  if (bidder.hasOwnProperty('type') && bidder.type === "trade") {
    tradeAnswers.value[bidder.id] = answer;
    storeProjectBidder(bidder.id);
  }
  else if (bidder.hasOwnProperty('type') && bidder.type === "package") {
    packageAnswer.value = answer;
    storeProjectBidder(null);
  }
  else {
    answers.value[bidder.id] = answer;
    updateProjectBidder(bidder.id);
  }
}

function storeProjectBidder(tradeId: number|null) {

  const payload: any = {
    confirmed: tradeId === null ? packageAnswer.value : tradeAnswers.value[tradeId],
    trade_id: tradeId,
  };

  bidPackageProjectBidderStore.store(<number>project.value?.id, <number>bidPackageId.value, payload)
    .then(() => {
      NotificationSuccess({
        message: `Response Updated`,
      });

      // emit('projectBidderUpdated');
    }).catch((error: any) => {
      if (error.hasOwnProperty('errors')) {
        errors.value = error.errors.map((error: any) => {
          if (error.key === 'confirmed') {
            return {
              ...error,
              forId: tradeId ?? 0,
            }
          }

          return error;
        });
      }
      else {
        NotificationError({
          message: error,
        });
      }
    }).finally(() => {
      isLoading.value = false;
    });
}

function updateProjectBidder(projectBidderId: number) {

  const payload: any = {
    confirmed: answers.value[projectBidderId],
  };

  isLoading.value = true;

  bidPackageProjectBidderStore.update(projectBidderId, <number>project.value?.id, <number>bidPackageId.value, payload)
    .then(() => {
      NotificationSuccess({
        message: `Response Updated`,
      });

      // emit('projectBidderUpdated');
    }).catch((error: any) => {
      if (error.hasOwnProperty('errors')) {
        errors.value = error.errors.map((error: any) => {
          if (error.key === 'confirmed') {
            return {
              ...error,
              forId: projectBidderId,
            }
          }

          return error;
        });
      }
      else {
        NotificationError({
          message: error,
        });
      }
    }).finally(() => {
      isLoading.value = false;
    });
}

</script>

<style lang="scss" scoped>

#bidding-tab {
  position: relative;

  .plan-room-loading {
    align-items: center;
    background: rgba(255, 255, 255, .75);
    display: flex;
    inset: 0;
    justify-content: center;
    position: absolute;
    z-index: 1;
  }

  .box {
    &.error {
      border-color: $danger;
    }

    .button {
      &.is-one-third {
        width: 33%;
      }
    }

    &.trade {
      overflow-x: auto;
      text-wrap: wrap;
      background: transparent;
      border: 0;
      box-shadow: none;
      padding: 0 !important;

      .bid-response-control {
        margin-bottom: 0;

        :deep(.button) {
          border-radius: 0;
        }

        :deep(.button:first-child) {
          border-bottom-left-radius: 4px;
          border-top-left-radius: 4px;
        }

        :deep(.button:last-child) {
          border-bottom-right-radius: 4px;
          border-top-right-radius: 4px;
        }
      }
    }

    &.overflow-scroll {
      overflow-x: auto;
    }

    .overflow-wrap {
      white-space: break-spaces;
    }

    .project-file-tree {
      padding: 0;

      :deep(.file-tree-tools) {
        gap: 0.5rem;
        padding: 0.5rem 0.75rem 0.6rem;
      }

      :deep(.file-tree-tools-row) {
        align-items: center;
        gap: 0.5rem;
      }

      :deep(.file-tree-tools-row:first-child) {
        border-bottom: 1px solid $grey-lighter;
        padding-bottom: 0.5rem;
      }

      :deep(.file-tree-toolset) {
        align-items: center;
        gap: 0.25rem;
      }

      :deep(.tree-tool) {
        align-items: center;
        display: inline-flex;
        gap: 0.35rem;
        min-height: 2rem;
        padding: 0.4rem 0.6rem;
      }
    }

    .project-files-empty {
      padding: 1rem;
    }

    &.project-files-container {
      min-height: 12rem;
    }
  }
}

@media (max-width: 1023px) {
  #bidding-tab {
    height: auto;

    .info {
      flex-direction: column;
      gap: 1rem;
    }
  }
}

</style>

<style lang="scss">

#bidding-tab {

  a {
    border: unset;
    padding: unset;
    justify-content: unset;
  }

  ul {
    border: unset;
  }
}

</style>
